import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  SourceBadge,
  Stat,
  StatusPill,
} from "@/components/dash/ui";

export default function NeedsAttentionPage() {
  const q = useQuery({ queryKey: ["attention"], queryFn: dashApi.attention, refetchInterval: 30_000 });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Needs Attention"
        description="Everything across the business that needs an executive decision, ranked by severity."
      />
      {q.isLoading ? (
        <LoadingState />
      ) : q.isError ? (
        <ErrorState error={q.error} />
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Critical" value={<span className="text-rose-300">{q.data?.counts.critical ?? 0}</span>} />
            <Stat label="Warning" value={<span className="text-amber-300">{q.data?.counts.warning ?? 0}</span>} />
            <Stat label="Info" value={<span className="text-sky-300">{q.data?.counts.info ?? 0}</span>} />
          </div>
          <Panel title={`Prioritised (${q.data?.total ?? 0})`}>
            {!q.data?.items.length ? (
              <EmptyState>✓ Nothing needs attention — all monitored conditions are within thresholds.</EmptyState>
            ) : (
              <ul className="divide-y divide-ink-500/40">
                {q.data.items.map((it) => (
                  <li key={it.rank} className="flex items-start gap-4 py-3">
                    <span className="w-5 text-center text-xs font-semibold text-slate-500">{it.rank}</span>
                    <StatusPill status={it.severity} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-100">{it.title}</p>
                        {it.source && <SourceBadge code={it.source} />}
                        {it.kind === "computed" && <span className="text-[10px] uppercase tracking-wide text-slate-600">derived</span>}
                      </div>
                      {it.detail && <p className="mt-0.5 text-xs text-slate-400">{it.detail}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
